import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import swal from 'sweetalert2';
import Loading from '../ui/Loading.js';
import Security from '../../commons/security/Security.js';
import * as Utils from '../../commons/utils/Utils';
import ProgramasPreventivosGrillaVehiculos from './ProgramasPreventivosGrillaVehiculos.jsx';
import ProgramasPreventivosHistorialModal from './ProgramasPreventivosHistorialModal.jsx';
import ProgramasPreventivosPublicarModal from './ProgramasPreventivosPublicarModal.jsx';

const ESTADO_CONFIG = {
    BORRADOR:  { label: 'Borrador',  color: '#6c757d' },
    PUBLICADO: { label: 'Publicado', color: '#28a745' },
    CERRADO:   { label: 'Cerrado',   color: '#343a40' },
};

const CATALOGO_REGLAS = {
    delta_critico:          'Delta crítico (más cercano a vencer va primero)',
    alto_costo_correctivos: 'Alto costo de correctivos (mayor gasto reciente va primero)',
    antiguedad_vehiculo:    'Antigüedad del vehículo (más antiguo va primero)',
    tiempo_sin_preventivo:  'Tiempo sin preventivo (más días sin mantenimiento va primero)',
    garantia:               'Vehículo en garantía (posterga al final)',
};

const ProgramasPreventivosDetalle = (props) => {
    const intl = useIntl();
    const id = props.match && props.match.params && props.match.params.id;

    const [redirectTo, setRedirectTo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [programa, setPrograma] = useState(null);
    const [tipoActivo, setTipoActivo] = useState(null);
    const [generando, setGenerando] = useState(false);
    const [refreshToken, setRefreshToken] = useState(0);
    const [mostrarPublicarModal, setMostrarPublicarModal] = useState(false);
    const [mostrarHistorialModal, setMostrarHistorialModal] = useState(false);

    useEffect(() => {
        if (!Security.hasPermission('PROGRAMAS_PREVENTIVOS_VISUALIZAR')) {
            setRedirectTo('/error');
            return;
        }
        Utils.getData('/programas-preventivos/' + id)
            .then(function(data) {
                setPrograma(data);
                if (data.tipos && data.tipos.length > 0) {
                    setTipoActivo(data.tipos[0].id);
                }
            })
            .catch(function() {
                setRedirectTo('/error');
            })
            .finally(function() {
                setLoading(false);
            });
    }, [id]);

    const handleGenerar = () => {
        swal.fire({
            title: intl.formatMessage({ id: 'programasPreventivosDetalle.generar_swal.titulo', defaultMessage: 'Generar grilla' }),
            text: intl.formatMessage({
                id: 'programasPreventivosDetalle.generar_swal.texto',
                defaultMessage: 'Se va a generar (o regenerar, si ya existía) la grilla de vehículos de este tipo con los datos actuales. Si ya había una grilla generada, se reemplaza.',
            }),
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: intl.formatMessage({ id: 'programasPreventivosDetalle.generar_swal.confirmar', defaultMessage: 'Generar' }),
            cancelButtonText: intl.formatMessage({ id: 'programasPreventivosAbm.btn_cancelar', defaultMessage: 'Cancelar' }),
        }).then(function(res) {
            if (!res.value) return;
            setGenerando(true);
            Utils.sendData('/programas-preventivos/' + id + '/generar', 'POST', {})
                .then(function() {
                    setRefreshToken(function(prev) { return prev + 1; });
                    swal.fire({
                        title: intl.formatMessage({ id: 'programasPreventivosDetalle.generar_swal.exito_titulo', defaultMessage: 'Listo' }),
                        text: intl.formatMessage({ id: 'programasPreventivosDetalle.generar_swal.exito_texto', defaultMessage: 'La grilla se generó correctamente.' }),
                        type: 'success',
                    });
                })
                .catch(function() {})
                .finally(function() { setGenerando(false); });
        });
    };

    const handlePublicar = () => {
        return Utils.sendData('/programas-preventivos/' + id + '/publicar', 'POST', {})
            .then(function() {
                setPrograma(function(prev) { return Object.assign({}, prev, { estado: 'PUBLICADO' }); });
            });
    };

    if (redirectTo) return <Redirect push to={redirectTo} />;
    if (loading) return <Loading />;
    if (!programa) return null;

    const estadoCfg = ESTADO_CONFIG[programa.estado] || { label: programa.estado, color: '#6c757d' };
    const tipoActivoData = programa.tipos.find(function(t) { return t.id === tipoActivo; });

    return (
        <React.Fragment>
            <div className="row">
                <div className="col-md-12">

                    {/* ── PASO 1 — Período Operativo ── */}
                    <div className="card pull-up">
                        <div className="card-content">
                            <div className="card-body">
                                <h4 className="form-section">
                                    <i className="icon-calendar"></i>
                                    <FormattedMessage id="programasPreventivosAbm.header_periodo_operativo" defaultMessage=" Período Operativo" />
                                    <span className="float-right">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary mr-2"
                                            onClick={function() { setMostrarHistorialModal(true); }}
                                        >
                                            <i className="fa fa-history"></i>{' '}
                                            <FormattedMessage id="programasPreventivosDetalle.btn_historial" defaultMessage="Ver Historial" />
                                        </button>
                                        {programa.estado === 'BORRADOR' && Security.hasPermission('PROGRAMAS_PREVENTIVOS_CREAR') && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary mr-2"
                                                onClick={function() { setMostrarPublicarModal(true); }}
                                            >
                                                <i className="fa fa-bullhorn"></i>{' '}
                                                <FormattedMessage id="programasPreventivosDetalle.btn_publicar" defaultMessage="Publicar" />
                                            </button>
                                        )}
                                        <span style={{
                                            backgroundColor: estadoCfg.color,
                                            color: '#fff',
                                            borderRadius: '4px',
                                            padding: '3px 12px',
                                            fontSize: '13px',
                                            fontWeight: 500,
                                        }}>
                                            {estadoCfg.label}
                                        </span>
                                    </span>
                                </h4>
                                <div className="row">
                                    <div className="col-md-8">
                                        <div className="form-group row">
                                            <label className="col-md-3 label-control col-form-label">
                                                <FormattedMessage id="programasPreventivosAbm.label_nombre" defaultMessage="Nombre" />:
                                            </label>
                                            <div className="col-md-9 col-form-label">
                                                <strong>{programa.nombre}</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="form-group row">
                                            <label className="col-md-6 label-control col-form-label">
                                                <FormattedMessage id="programasPreventivosAbm.label_fecha_inicio" defaultMessage="Fecha de inicio" />:
                                            </label>
                                            <div className="col-md-6 col-form-label">
                                                {programa.fecha_inicio}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group row">
                                            <label className="col-md-6 label-control col-form-label">
                                                <FormattedMessage id="programasPreventivosAbm.label_fecha_fin" defaultMessage="Fecha de fin" />:
                                            </label>
                                            <div className="col-md-6 col-form-label">
                                                {programa.fecha_fin}
                                            </div>
                                        </div>
                                    </div>
                                    {programa.fecha_limite_publicacion && (
                                        <div className="col-md-4">
                                            <div className="form-group row">
                                                <label className="col-md-6 label-control col-form-label">
                                                    <FormattedMessage id="programasPreventivosAbm.label_fecha_limite" defaultMessage="Fecha límite de publicación" />:
                                                </label>
                                                <div className="col-md-6 col-form-label">
                                                    {programa.fecha_limite_publicacion}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── PASO 2 — Tipos de vehículo ── */}
                    <div className="card pull-up">
                        <div className="card-content">
                            <div className="card-body">
                                <h4 className="form-section">
                                    <i className="icon-car"></i>
                                    <FormattedMessage id="programasPreventivosAbm.header_tipos_vehiculo" defaultMessage=" Tipos de vehículo" />
                                </h4>
                                <table className="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th><FormattedMessage id="programasPreventivosAbm.col_tipo" defaultMessage="Tipo de vehículo" /></th>
                                            <th style={{ width: '220px' }}><FormattedMessage id="programasPreventivosAbm.col_presupuesto" defaultMessage="Presupuesto de referencia" /></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {programa.tipos.map(function(tipo) {
                                            return (
                                                <tr key={tipo.id}>
                                                    <td>{tipo.nombre_tipo}</td>
                                                    <td>
                                                        {tipo.presupuesto_referencia
                                                            ? '$' + parseFloat(tipo.presupuesto_referencia).toLocaleString('es-AR')
                                                            : <span className="text-muted">—</span>
                                                        }
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── PASO 3 — Reglas de priorización ── */}
                    <div className="card pull-up">
                        <div className="card-content">
                            <div className="card-body">
                                <h4 className="form-section">
                                    <i className="fa fa-sort-amount-asc"></i>
                                    <FormattedMessage id="programasPreventivosAbm.header_priorizacion" defaultMessage=" Reglas de priorización" />
                                </h4>

                                <ul className="nav nav-tabs mb-3">
                                    {programa.tipos.map(function(tipo) {
                                        var activo = tipoActivo === tipo.id;
                                        return (
                                            <li key={tipo.id} className="nav-item">
                                                <a
                                                    className={'nav-link' + (activo ? ' active' : '')}
                                                    href="#!"
                                                    onClick={function(e) { e.preventDefault(); setTipoActivo(tipo.id); }}
                                                >
                                                    {tipo.nombre_tipo}
                                                    {tipo.reglas.length > 0 && (
                                                        <span className="badge badge-primary ml-1">{tipo.reglas.length}</span>
                                                    )}
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>

                                {tipoActivoData && (
                                    tipoActivoData.reglas.length === 0 ? (
                                        <p className="text-muted">
                                            <FormattedMessage id="programasPreventivosAbm.sin_reglas" defaultMessage="Sin reglas activas — la grilla no tendrá un orden de priorización específico." />
                                        </p>
                                    ) : (
                                        <div style={{ maxWidth: '560px' }}>
                                            {tipoActivoData.reglas.map(function(regla, idx) {
                                                return (
                                                    <div key={regla.regla} className="d-flex align-items-center mb-2 p-2" style={{ background: '#f8f9fa', borderRadius: '4px' }}>
                                                        <span className="badge badge-primary mr-2" style={{ minWidth: '24px' }}>{idx + 1}</span>
                                                        <span style={{ fontSize: '13px', flexGrow: 1 }}>
                                                            {CATALOGO_REGLAS[regla.regla] || regla.regla}
                                                        </span>
                                                        {regla.regla === 'garantia' && regla.anio_garantia && (
                                                            <span className="badge badge-secondary ml-2" style={{ fontSize: '12px' }}>
                                                                hasta {regla.anio_garantia}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Grilla de vehículos generada (VEC-3331) ── */}
                    <div className="card pull-up">
                        <div className="card-content">
                            <div className="card-body">
                                <h4 className="form-section">
                                    <i className="fa fa-table"></i>
                                    <FormattedMessage id="programasPreventivosDetalle.header_grilla" defaultMessage=" Grilla de vehículos" />
                                    {Security.hasPermission('PROGRAMAS_PREVENTIVOS_CREAR') && (
                                        <span className="float-right">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                disabled={generando}
                                                onClick={handleGenerar}
                                            >
                                                <i className="fa fa-refresh"></i>{' '}
                                                <FormattedMessage id="programasPreventivosDetalle.btn_generar" defaultMessage="Generar / Regenerar" />
                                            </button>
                                        </span>
                                    )}
                                </h4>

                                {programa.resumen && (
                                    <p className="mb-3" style={{ fontSize: '14px' }}>
                                        <FormattedMessage id="programasPreventivosDetalle.resumen_total" defaultMessage="Total programa:" />{' '}
                                        <strong>{programa.resumen.total}</strong>
                                        <span className="mx-2 text-muted">|</span>
                                        <FormattedMessage id="programasPreventivosDetalle.resumen_incluidas" defaultMessage="Incluidas:" />{' '}
                                        <strong style={{ color: '#28a745' }}>{programa.resumen.incluidos}</strong>
                                        <span className="mx-2 text-muted">|</span>
                                        <FormattedMessage id="programasPreventivosDetalle.resumen_excluidas" defaultMessage="Excluidas:" />{' '}
                                        <strong style={{ color: '#dc3545' }}>{programa.resumen.excluidos}</strong>
                                        <span className="mx-2 text-muted">|</span>
                                        <FormattedMessage id="programasPreventivosDetalle.resumen_con_alertas" defaultMessage="Con alertas:" />{' '}
                                        <strong style={{ color: '#f8a038' }}>{programa.resumen.con_alertas}</strong>
                                    </p>
                                )}

                                <ul className="nav nav-tabs mb-3">
                                    {programa.tipos.map(function(tipo) {
                                        var activo = tipoActivo === tipo.id;
                                        return (
                                            <li key={tipo.id} className="nav-item">
                                                <a
                                                    className={'nav-link' + (activo ? ' active' : '')}
                                                    href="#!"
                                                    onClick={function(e) { e.preventDefault(); setTipoActivo(tipo.id); }}
                                                >
                                                    {tipo.nombre_tipo}
                                                    {tipo.resumen && (
                                                        <span className="badge badge-secondary ml-1">
                                                            {tipo.resumen.incluidos}/{tipo.resumen.excluidos}
                                                        </span>
                                                    )}
                                                </a>
                                            </li>
                                        );
                                    })}
                                </ul>

                                <ProgramasPreventivosGrillaVehiculos
                                    tipoId={tipoActivo}
                                    refreshToken={refreshToken}
                                    periodoOperativo={{ fecha_inicio: programa.fecha_inicio, fecha_fin: programa.fecha_fin }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Botón volver ── */}
                    <div className="card pull-up">
                        <div className="card-content">
                            <div className="card-body">
                                <div className="text-cd text-right">
                                    <button type="button" className="btn btn-secondary" onClick={function() { setRedirectTo('/programas-preventivos'); }}>
                                        <i className="fa fa-arrow-circle-left"></i>{' '}
                                        <FormattedMessage id="programasPreventivosDetalle.btn_volver" defaultMessage="Volver" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <ProgramasPreventivosPublicarModal
                open={mostrarPublicarModal}
                programa={programa}
                onClose={function() { setMostrarPublicarModal(false); }}
                onConfirmar={handlePublicar}
            />

            <ProgramasPreventivosHistorialModal
                open={mostrarHistorialModal}
                programaId={id}
                onClose={function() { setMostrarHistorialModal(false); }}
            />
        </React.Fragment>
    );
};

export default ProgramasPreventivosDetalle;
